import type { PointArray } from '../types/contour-definition-array-type';
/**
 * Applies moving average smoothing to a contour.
 * Each point is replaced by the average of itself and its neighbors within the window.
 * Simpler and faster than Chaikin's algorithm, preserves more detail at edges.
 *
 * @param pointArray - The input contour points
 * @param windowSize - Size of the moving average window (default: 3, must be odd)
 * @returns Smoothed contour
 */
export function movingAverageSmoothing(
  pointArray: PointArray,
  windowSize: number = 3,
): PointArray {
  if (pointArray.length < windowSize) return pointArray;

  const smoothed: PointArray = [];
  const half = Math.floor(windowSize / 2);

  const isClosed
    = pointArray.length > 0
      && pointArray[0].x === pointArray[pointArray.length - 1].x
      && pointArray[0].y === pointArray[pointArray.length - 1].y;

  const len = isClosed ? pointArray.length - 1 : pointArray.length;

  for (let i = 0; i < len; i++) {
    let sumX = 0;
    let sumY = 0;
    let count = 0;

    for (let j = -half; j <= half; j++) {
      let idx = i + j;

      if (isClosed) {
        idx = (idx + len) % len;
        sumX += pointArray[idx].x;
        sumY += pointArray[idx].y;
        count++;
      }
      else if (idx >= 0 && idx < len) {
        sumX += pointArray[idx].x;
        sumY += pointArray[idx].y;
        count++;
      }
    }

    smoothed.push({
      x: sumX / count,
      y: sumY / count,
    });
  }

  // If closed, close the smoothed loop
  if (isClosed && smoothed.length > 0) {
    smoothed.push({ ...smoothed[0] });
  }

  return smoothed;
}

/**
 * Applies Gaussian smoothing to a contour.
 * Uses a Gaussian kernel to weight nearby points, providing smoother results than moving average.
 *
 * @param pointArray - The input contour points
 * @param sigma - Standard deviation of the Gaussian kernel (default: 1.5)
 * @returns Smoothed contour
 */
export function gaussianSmoothing(
  pointArray: PointArray,
  sigma: number = 1.5,
): PointArray {
  if (pointArray.length < 3) return pointArray;

  const kernel: number[] = [];
  const kernelSize = 5;
  const half = Math.floor(kernelSize / 2);

  // Generate Gaussian kernel
  let sum = 0;
  for (let i = -half; i <= half; i++) {
    const val = Math.exp(-(i * i) / (2 * sigma * sigma));
    kernel.push(val);
    sum += val;
  }

  // Normalize kernel

  kernel.forEach((_, i) => {
    kernel[i] /= sum;
  });

  const smoothed: PointArray = [];

  const isClosed
    = pointArray.length > 0
      && pointArray[0].x === pointArray[pointArray.length - 1].x
      && pointArray[0].y === pointArray[pointArray.length - 1].y;

  const len = isClosed ? pointArray.length - 1 : pointArray.length;

  for (let i = 0; i < len; i++) {
    let x = 0;
    let y = 0;
    let totalWeight = 0;

    for (let j = -half; j <= half; j++) {
      let idx = i + j;

      if (isClosed) {
        idx = (idx + len) % len;
      }
      else if (idx < 0 || idx >= len) {
        continue;
      }

      const weight = kernel[j + half];
      x += pointArray[idx].x * weight;
      y += pointArray[idx].y * weight;
      totalWeight += weight;
    }

    smoothed.push({
      x: x / totalWeight,
      y: y / totalWeight,
    });
  }

  if (isClosed && smoothed.length > 0) {
    smoothed.push({ ...smoothed[0] });
  }

  return smoothed;
}
